import { Mutation, Query } from "@apollo/client/react/components";
import React, { ReactElement } from "react";
import ChangeSubscriptionPlan from "~/lib/mutations/ChangeSubscriptionPlan";
import {
  IChangeSubscriptionPlanMutation,
  IChangeSubscriptionPlanMutationVariables,
} from "~/lib/mutations/__generated__/ChangeSubscriptionPlan.generated";
import StripePlansQuery from "~/lib/queries/StripePlansQuery";
import { ICompanyAccountBillingInfoQuery } from "~/lib/queries/__generated__/CompanyAccountBillingInfoQuery.generated";
import {
  IStripePlansQuery,
  IStripePlansQueryVariables,
} from "~/lib/queries/__generated__/StripePlansQuery.generated";
import { IPlanRecurrency } from "~/types/types";
import { refetchQueries } from "~/lib/apollo/RefetchQueriesFixLink";

type TStripePlan = NonNullable<NonNullable<ICompanyAccountBillingInfoQuery["company"]>["stripePlan"]>;

interface IChangeBillingRecurrencyChildrenState {
  isChangingPlans: boolean;
  canChangeToYearly: boolean;
  canChangeToMonthly: boolean;
  toPlan: TStripePlan | null;
  changeToMonthly: () => void;
  changeToYearly: () => void;
}

interface IChangeBillingRecurrencyProps {
  currentStripePlan: TStripePlan | null;
  children: (state: IChangeBillingRecurrencyChildrenState) => ReactElement | null;
}

export default function ChangeBillingRecurrency({ currentStripePlan, children }: IChangeBillingRecurrencyProps) {
  if (!currentStripePlan) {
    return null;
  }

  return (
    <Query<IStripePlansQuery, IStripePlansQueryVariables> query={StripePlansQuery}>
      {({ data, loading }) => {
        return (
          <Mutation<IChangeSubscriptionPlanMutation, IChangeSubscriptionPlanMutationVariables>
            mutation={ChangeSubscriptionPlan}
            awaitRefetchQueries={true}
            refetchQueries={refetchQueries(["Plans"])}
          >
            {(changeSubscriptionPlan, { loading: isChangingPlans }) => {
              if (!data || loading) {
                return children({
                  isChangingPlans: false,
                  canChangeToYearly: false,
                  canChangeToMonthly: false,
                  toPlan: null,
                  changeToYearly: () => {},
                  changeToMonthly: () => {},
                });
              }
              const monthlyPlan = data.stripePlans.find((candidate) => {
                return (
                  candidate.productId === currentStripePlan.productId &&
                  candidate.recurrency === IPlanRecurrency.Monthly
                );
              });

              const yearlyPlan = data.stripePlans.find((candidate) => {
                return (
                  candidate.productId === currentStripePlan.productId && candidate.recurrency === IPlanRecurrency.Yearly
                );
              });

              const canChangeToYearly = Boolean(yearlyPlan && currentStripePlan.recurrency === IPlanRecurrency.Monthly);
              const canChangeToMonthly = Boolean(
                monthlyPlan && currentStripePlan.recurrency === IPlanRecurrency.Yearly,
              );

              return children({
                isChangingPlans,
                canChangeToYearly,
                canChangeToMonthly,
                toPlan: canChangeToYearly ? yearlyPlan || null : monthlyPlan || null,
                changeToYearly: () => {
                  if (canChangeToYearly && yearlyPlan) {
                    changeSubscriptionPlan({
                      variables: {
                        stripePlanId: yearlyPlan.planId,
                      },
                    });
                  }
                },
                changeToMonthly: () => {
                  if (canChangeToMonthly && monthlyPlan) {
                    changeSubscriptionPlan({
                      variables: {
                        stripePlanId: monthlyPlan.planId,
                      },
                    });
                  }
                },
              });
            }}
          </Mutation>
        );
      }}
    </Query>
  );
}
